export default class FreeWheelAd {
  constructor(respType) {
    this._respType = respType;
  }

  // Used to identify a FreeWheel customer.
  // These values are unique within the scope of the FreeWheel universe and are assigned by FreeWheel.
  // We will have a single networkId to use across all platforms and brands.
  get networkId() {
    return this._networkId;
  }

  set networkId(networkId) {
    this._networkId = networkId;
  }

  // Player profile pre-configured by FW
  // Used to describe the playback capabilities of a given player on a platform.
  // A profile is just a key used to look up the capabilities of a player.
  // We may have different profiles for different platforms, but they will be shared across all brands.
  get profile() {
    return this._profile;
  }

  set profile(profile) {
    this._profile = profile;
  }

  // Used to describe where video is being played.
  // FreeWheel intends that this value describe where within an application or web site video was started.
  // For example, one siteSection may refer to content that is auto-played on startup.
  // Another may refer video that was started from a "my favorites" list.
  // We will probably have a single siteSection for our player.
  get siteSection() {
    return this._siteSection;
  }

  set siteSection(siteSection) {
    this._siteSection = siteSection;
  }

  // Custom content video asset ID. What video is being played
  get videoAssetId() {
    return this._videoAssetId;
  }

  set videoAssetId(videoAssetId) {
    this._videoAssetId = videoAssetId;
  }

  // Used to refer to all ads that should be played or displayed within a particular time slot.
  // This includes video ads as well as companion banner ads.
  // get adPod() {
  //     return this._adPod;
  // }

  // set adPod(adPod) {
  //     this._adPod = adPod;
  // }

  // Indicates an ad opportunity. May refer to a location for a banner ad, or a time reserved for video ads.
  get adSlots() {
    return this._adSlots || [];
  }

  set adSlots(adSlots) {
    this._adSlots = adSlots.map(adSlot =>
      Object.assign(new FreewheelAdSlot(), {
        ...adSlot,
      }),
    );
  }

  // The networkId of the owner of the siteSection. This will be our networkID in all cases.
  get siteSectionOwnerId() {
    return this._siteSectionOwnerId || this._networkId;
  }

  set siteSectionOwnerId(siteSectionOwnerId) {
    this._siteSectionOwnerId = siteSectionOwnerId;
  }

  // The networkId of the owner of the content. This will be the networkId of the programmer.
  get assetOwnerNetworkId() {
    return this._assetOwnerNetworkId || this._networkId;
  }

  set assetOwnerNetworkId(assetOwnerNetworkId) {
    this._assetOwnerNetworkId = assetOwnerNetworkId;
  }

  // // A programmer-supplied default assetID that will be used if we can't get an assetId from the programmer.
  // get fallbackId() {
  //     return this._fallbackId;
  // }

  // set fallbackId(fallbackId) {
  //     this._fallbackId = fallbackId;
  // }

  // // At times, FreeWheel will refer us to another service to get an ad for a particular adPod. If that referral cannot be correctly resolved, the we should play the fallback ad
  // get fallbackAd() {
  //     return this._fallbackAd;
  // }

  // set fallbackAd(fallbackAd) {
  //     this._fallbackAd = fallbackAd;
  // }

  // // Defines a particular ad. Examples would be individual commercials or banner ads
  // get creative() {
  //     return this._creative;
  // }

  // set creative(creative) {
  //     this._creative = creative;
  // }

  // // Defines the implementation of a "creative." One "creative" will have several "creativeRenditions." For example, one commercial could be implemented as an HLS stream, an mp4 file, and an mpeg2 file. Each of these would be considered separate "creativeRenditions" of the the same "creative."
  // get creativeRendition() {
  //     return this._creativeRendition;
  // }

  // set creativeRendition(creativeRendition) {
  //     this._creativeRendition = creativeRendition;
  // }

  get adServerUrl() {
    return (this._adServerUrl !== 'undefined' && this._adServerUrl) || 'https://5d40a.s.fwmrm.net/';
  }

  set adServerUrl(adServerUrl) {
    this._adServerUrl = adServerUrl;
  }

  // A player-generated HTTP GET URL that requests information from a FreeWheel MRM
  get adRequest() {
    return this._adRequest;
  }

  set adRequest(adRequest) {
    this._adRequest = adRequest;
  }

  get randomNumber() {
    if (!this._randomNumber) {
      this._randomNumber = getRandom();
    }

    return this._randomNumber;
  }

  // Get ad slot URL arguments
  get adSlotArgs() {
    return this.adSlots.map(adSlot => adSlot.urlArgs).join('');
  }

  get responseType() {
    if (!this._respType) {
      this._respType = 'vmap1';
    }
    return this._respType;
  }

  set responseType(responseType) {
    this._respType = responseType;
  }

  get visitorIp() {
    return this._visitorIp;
  }

  set visitorIp(ip) {
    this._visitorIp = ip;
  }

  get deviceId() {
    return this._deviceId;
  }

  set deviceId(deviceId) {
    this._deviceId = deviceId;
  }

  get mode() {
    return this._mode;
  }

  set mode(mode) {
    this._mode = mode;
  }

  get uuid() {
    return this._uuid;
  }

  set uuid(uuid) {
    this._uuid = uuid;
  }

  get reset() {
    return this._isReset ? '+rste' : '';
  }

  set reset(isReset) {
    this._isReset = isReset;
  }

  get limitAdTracking() {
    return this._limitAdTracking;
  }

  set limitAdTracking(limitAdTracking) {
    this._limitAdTracking = limitAdTracking;
  }

  // A player-generated HTTP GET URL that requests information from a FreeWheel MRM
  get url() {
    const freewheelUrl = [
      `nw=${this.networkId}`, // network ID
      `asnw=${this.assetOwnerNetworkId}`, // Asset network ID or network ID if not available
      `ssnw=${this.siteSectionOwnerId}`, // Site section network ID or network ID if not available
      `csid=${this.siteSection}`, // Custom site section ID where the video is played
      `vprn=${this.randomNumber}`,
      `caid=${this.videoAssetId}`, // Custom content video asset ID. What video is being played
      `pvrn=${this.randomNumber}`,
      `prof=${this.profile}`, // player profile pre-configured by FW
      `metr=1031`, // metr value 7: the ads can send impressions, 4 quartiles and click callbacks. For backward compatibility reasons, 1 works the same as 7.
      `flag=+emcr+unks+fbad+aeti+exvt+play+sltp+slcb+sync${this.reset}`, // expectedMultipleCreativeRenditions
      `vdur=${this.clipDurationSeconds}`, // Video duration
      `vcid=${this.uuid}`, // custom visitor ID equivalent to visitor/@customId attribute
      `ssto=${this.uuid}`, // unique ID for exclusivity per user
      `vip=${this.visitorIp}`,
      `resp=${this.responseType};`, // Response type
      `_fw_vcid2=${this.networkId}:${this.uuid}`, // freewheel server-side user ID, used for cookies and audience targeting
      '_fw_ae=d51f95cd19fecba66558f490554838ee', // Dish hardcoded TV Everywhere ID
      '_fw_dpr=1.00',
      `_fw_is_lat=${this.limitAdTracking ? 1 : 0}`, // LAT restricts advertisers from targeting based on user behavior.
      '_fw_enable_lat_fc=1', // hardcoded frequency capping (always enabled)
    ];

    if (this.deviceId) {
      freewheelUrl.push(`_fw_did=${this.deviceId}`);
    }
    if (this.mode === 'live') {
      freewheelUrl.splice(13, 0, 'mode=live', `vrdu=${this.clipDurationSeconds}`, `vdty=variable`);
    }

    return `${this.adServerUrl}ad/g/1?${freewheelUrl.reduce(
      (accumulator, currentValue) =>
        accumulator + currentValue + (currentValue[currentValue.length - 1] === ';' ? '' : '&'),
      '',
    )}${this.adSlotArgs}`;
  }
}

export class FreewheelAdSlot {
  // Identifies the slot uniquely within an asset
  get slotId() {
    return this._slotId;
  }

  set slotId(slotId) {
    this._slotId = slotId || `Ad_${getRandom}`;
  }

  // Identifies when the ad will be played/displayed
  // Valid values: preroll, midroll, postroll, display, etc
  get slotPosition() {
    return this._slotPosition;
  }

  set slotPosition(slotPosition) {
    this._slotPosition = slotPosition;
  }

  // Identifies if the slot is temporal or not
  // a: temporal slot (default)
  // s: site-section, non-temporal
  // p: video-player non-temporal
  get slotType() {
    return this._slotType || 'a';
  }

  set slotType(slotType) {
    this._slotType = slotType;
  }

  // Identifies when the slot will occur within the asset
  get timePosition() {
    return this._timePosition;
  }

  set timePosition(timePosition) {
    this._timePosition = timePosition || 0;
  }

  // Min ad duration (secs)
  get minDuration() {
    return this._minDuration || 0;
  }

  set minDuration(minDuration) {
    this._minDuration = minDuration;
  }

  // Max ad duration (secs)
  get maxDuration() {
    return this._maxDuration || 0;
  }

  set maxDuration(maxDuration) {
    this._maxDuration = maxDuration;
  }

  get urlArgs() {
    const args = [
      `;slid=${this.slotId}`,
      `slau=${this.slotPosition}`,
      `ptgt=${this.slotType}`,
      `tpos=${this.timePosition}`,
    ];

    if (this.minDuration || this.maxDuration) {
      args.push(`mind=${this.minDuration}`, `maxd=${this.maxDuration}`);
    }

    return args.join('&');
  }

  get responseType() {
    return this._responseType || 'vmap1';
  }

  set responseType(responseType) {
    this._responseType = responseType;
  }
}

const getRandom = () => Math.ceil(Math.random() * 9999999999);
